import type { Schema } from 'effect';
import { Schema as SchemaValue } from 'effect';

import {
  OrderItemjsonldsyliusshopcartshowsyliusshopcartshowdetail,
  Orderjsonldsyliusshopcartshowsyliusshopcartshowdetail,
  Orderjsonldsyliusshoporderaccountshowsyliusshopcartshowsyliusshopcartshowdetailsyliusshopcartshowpaid,
  Orderjsonldsyliusshoporderindex,
} from '../gen/rcprofi';
import {
  OrderDetail as OrderDetailBase,
  OrderDetailPaid as OrderDetailPaidBase,
  OrderIndex as OrderIndexBase,
  OrderItem as OrderItemBase,
} from '@vs-shop/schemas/branded/OrderSchema';
import { overrideSchema } from '@vs-shop/schemas/openapi/overrideSchema';
import * as AsyncResult from 'effect/unstable/reactivity/AsyncResult';

/**
 * RcProfi's strict order boundary. The shared base leaves the generated cart
 * closure in documented slots; applying its branded scalar fields back onto
 * the generated roots preserves the existing decoded and encoded contracts.
 */
export const OrderItem = overrideSchema(OrderItemjsonldsyliusshopcartshowsyliusshopcartshowdetail, {
  '@id': OrderItemBase.fields['@id'],
  id: OrderItemBase.fields.id,
  quantity: OrderItemBase.fields.quantity,
  unitPrice: OrderItemBase.fields.unitPrice,
  discountedUnitPrice: OrderItemBase.fields.discountedUnitPrice,
  frontUnitPriceWithVat: OrderItemBase.fields.frontUnitPriceWithVat,
  frontItemsPriceWithVat: OrderItemBase.fields.frontItemsPriceWithVat,
  frontUnitPriceWithoutVatAfterDiscount: OrderItemBase.fields.frontUnitPriceWithoutVatAfterDiscount,
  frontItemsPriceWithVatAfterDiscount: OrderItemBase.fields.frontItemsPriceWithVatAfterDiscount,
  frontItemsPriceWithoutVatAfterDiscount:
    OrderItemBase.fields.frontItemsPriceWithoutVatAfterDiscount,
}).annotate({ identifier: 'OrderItem' });
export type OrderItem = Schema.Schema.Type<typeof OrderItem>;

export const OrderDetail = overrideSchema(Orderjsonldsyliusshopcartshowsyliusshopcartshowdetail, {
  '@id': OrderDetailBase.fields['@id'],
  items: SchemaValue.Array(OrderItem),
  tokenValue: OrderDetailBase.fields.tokenValue,
  number: OrderDetailBase.fields.number,
  state: OrderDetailBase.fields.state,
  checkoutState: OrderDetailBase.fields.checkoutState,
  paymentState: OrderDetailBase.fields.paymentState,
  shippingState: OrderDetailBase.fields.shippingState,
  globalState: OrderDetailBase.fields.globalState,
  itemsTotal: OrderDetailBase.fields.itemsTotal,
  total: OrderDetailBase.fields.total,
  totalWithoutTax: OrderDetailBase.fields.totalWithoutTax,
  itemsTotalWithoutTax: OrderDetailBase.fields.itemsTotalWithoutTax,
  orderPromotionTotal: OrderDetailBase.fields.orderPromotionTotal,
  orderPromotionTotalWithoutTax: OrderDetailBase.fields.orderPromotionTotalWithoutTax,
  taxTotal: OrderDetailBase.fields.taxTotal,
  frontAllItemsPriceWithVatAfterDiscount:
    OrderDetailBase.fields.frontAllItemsPriceWithVatAfterDiscount,
  frontAllItemsPriceWithoutVatAfterDiscount:
    OrderDetailBase.fields.frontAllItemsPriceWithoutVatAfterDiscount,
  frontTotalWithVat: OrderDetailBase.fields.frontTotalWithVat,
  frontTotalWithoutVat: OrderDetailBase.fields.frontTotalWithoutVat,
  frontTotalTaxAppliedAfterDiscount: OrderDetailBase.fields.frontTotalTaxAppliedAfterDiscount,
  frontShipmentWithVat: OrderDetailBase.fields.frontShipmentWithVat,
  frontShipmentDiscountAsNominalValue: OrderDetailBase.fields.frontShipmentDiscountAsNominalValue,
  frontShipmentWithoutVatAfterDiscount: OrderDetailBase.fields.frontShipmentWithoutVatAfterDiscount,
  frontPaymentWithVat: OrderDetailBase.fields.frontPaymentWithVat,
  frontPaymentDiscountAsNominalValue: OrderDetailBase.fields.frontPaymentDiscountAsNominalValue,
  frontPaymentWithoutVatAfterDiscount: OrderDetailBase.fields.frontPaymentWithoutVatAfterDiscount,
}).annotate({ identifier: 'OrderDetail' });
export type OrderDetail = Schema.Schema.Type<typeof OrderDetail>;

export const OrderIndex = overrideSchema(Orderjsonldsyliusshoporderindex, {
  '@id': OrderIndexBase.fields['@id'],
  tokenValue: OrderIndexBase.fields.tokenValue,
  number: OrderIndexBase.fields.number,
  state: OrderIndexBase.fields.state,
  globalState: OrderIndexBase.fields.globalState,
}).annotate({ identifier: 'OrderIndex' });
export type OrderIndex = Schema.Schema.Type<typeof OrderIndex>;

export const OrderDetailPaid = overrideSchema(
  Orderjsonldsyliusshoporderaccountshowsyliusshopcartshowsyliusshopcartshowdetailsyliusshopcartshowpaid,
  {
    '@id': OrderDetailPaidBase.fields['@id'],
    tokenValue: OrderDetailPaidBase.fields.tokenValue,
    number: OrderDetailPaidBase.fields.number,
    invoiceId: OrderDetailPaidBase.fields.invoiceId,
    state: OrderDetailPaidBase.fields.state,
    checkoutState: OrderDetailPaidBase.fields.checkoutState,
    paymentState: OrderDetailPaidBase.fields.paymentState,
    shippingState: OrderDetailPaidBase.fields.shippingState,
    globalState: OrderDetailPaidBase.fields.globalState,
  }
).annotate({ identifier: 'OrderDetailPaid' });
export type OrderDetailPaid = Schema.Schema.Type<typeof OrderDetailPaid>;

/**
 * Strict-typed aliases for the account-orders surfaces. The package-side
 * `@vs-shop/schemas/order` variants type the slot arms loosely; app consumers
 * need the generated-root strict shapes these tightened schemas carry.
 */
export type OrderItemType = OrderIndex;
export type OrderListType = ReadonlyArray<OrderIndex>;

export const OrderDetailOptionSchema = SchemaValue.Option(OrderDetail);

export const OrderDetailResultSchema = AsyncResult.Schema({
  success: OrderDetailOptionSchema,
  // TODO: proper schema for error
  error: SchemaValue.Any,
});

// `OrderDetailResultJsonCodec` / `encodeOrderDetailResult` moved into the engine
// order atom factory (`packages/engine/src/module/order/makeOrderAtoms.ts`) as
// part of the seam-C W2.1 migration; they are produced by the tenant bind and
// re-exported from `@rcprofi/effect/bind/order`. `OrderDetailResultSchema` stays
// here as the tenant schema source the bind feeds into the factory.
